import BoltOnRenewalSummary from './teams/components/BoltOnRenewalSummary';
import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { useAuth } from '../auth/AuthContext';
import { db, functions } from '../firebase-client';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFunctionUrl } from '../utils/functionURLs';
import { getCurrencyMeta } from '../utils/currency';
import { calculateTotalFees } from '../utils/calculateTotalFees';
import { getPlatformFeeMinor } from '../utils/fees';
import { resolveCycleBasePricing } from '../utils/subscriptionPricing';
import LoadingModal from './ui/LoadingModal';
import { useOnlineRefreshSignal } from '../utils/onlineStatus';
import { cadenceFrequencyLabel, getMembershipCadenceTransition } from '../utils/membershipCadenceTransition';

function normalizeMembershipPlanType(value) {
  return value === 'family_tier' ? 'family_tier' : 'individual';
}

function normalizeStringList(values) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map(value => typeof value === 'string' ? value.trim() : '').filter(Boolean)));
}

function resolveManualMembershipRenewal({
  settings = {},
  setup = {},
  pendingRenewal = null
}) {
  if (pendingRenewal && Number.isFinite(Number(pendingRenewal.amountMinor))) {
    return {
      amountMinor: Number(pendingRenewal.amountMinor),
      currency: pendingRenewal.currency || String(setup?.currency || 'GBP').toUpperCase(),
      dueDate: pendingRenewal.dueDate || null
    };
  }

  const currency = String(setup?.currency || settings?.currency || 'GBP').toUpperCase();
  const interval = settings?.interval === 'month' ? 'month' : 'year';
  const intervalCount = Math.max(1, Number(settings?.intervalCount || setup?.fixedInterval?.count || 1));
  const cadenceMode = setup?.cadenceMode || 'fixed';
  const discountPct = cadenceMode === 'member_choice'
    ? Number(interval === 'month' ? setup?.memberChoice?.monthly?.discountPct || 0 : setup?.memberChoice?.yearly?.discountPct || 0)
    : 0;
  const membershipPlanType = normalizeMembershipPlanType(settings?.membershipPlanType);
  const memberCount = membershipPlanType === 'family_tier'
    ? Math.max(1, normalizeStringList(settings?.membershipCoveredMemberIds).length)
    : 1;
  const appliedTags = membershipPlanType === 'family_tier'
    ? []
    : normalizeStringList([
        ...(settings?.membershipPricingTag ? [settings.membershipPricingTag] : []),
        ...normalizeStringList(settings?.membershipAdminTagNames)
      ]);
  const familyTiers = Array.isArray(setup?.familyTiers) ? setup.familyTiers : [];
  const selectedTier = familyTiers.find(tier => {
    const tierId = typeof tier?.id === 'string' ? tier.id.trim() : '';
    return tierId && tierId === settings?.membershipPlanTierId;
  }) || null;
  const pricing = resolveCycleBasePricing({
    baseRate: membershipPlanType === 'family_tier' ? Number(selectedTier?.baseRate || 0) : Number(setup?.baseRate || 0),
    baseRateYearly: membershipPlanType === 'family_tier'
      ? (selectedTier?.baseRateYearly ?? (Number(selectedTier?.baseRate || 0) * 12))
      : setup?.baseRateYearly,
    tagOverrides: membershipPlanType === 'family_tier' ? {} : (setup?.tagOverrides || {}),
    tagOverridesYearly: membershipPlanType === 'family_tier' ? {} : (setup?.tagOverridesYearly || {}),
    appliedTags,
    preferredTag: membershipPlanType === 'family_tier' ? '' : (settings?.membershipPricingTag || ''),
    cadenceUnit: interval,
    cadenceCount: intervalCount
  });
  const feeHeadcount = (membershipPlanType === 'family_tier' ? memberCount : 1) + Math.max(0, Number(settings.additionalAccountActiveCount || 0));
  const effectivePlatformFeeMinor = Number.isFinite(Number(setup?.platformFeeMinor))
    ? Number(setup.platformFeeMinor) * feeHeadcount
    : getPlatformFeeMinor({
        currency,
        teamFeeCfg: setup,
        memberNestFeePence: Number.isFinite(Number(setup?.memberNestFeePence)) ? Number(setup.memberNestFeePence) : undefined
      }) * feeHeadcount;
  const fees = calculateTotalFees({
    baseAmount: Math.round(Number(pricing?.baseForCycle || 0)),
    cadence: {
      unit: interval,
      count: intervalCount,
      discountPct
    },
    platformFeeHandling: setup?.platformFeeHandling || 'deduct',
    stripeFeeHandling: setup?.stripeFeeHandling || 'deduct',
    currency,
    platformFeeMinor: effectivePlatformFeeMinor
  });

  return {
    amountMinor: Number(fees?.finalAmount || 0),
    currency,
    dueDate: typeof settings?.nextDueDate?.toDate === 'function' ? settings.nextDueDate.toDate() : settings?.nextDueDate ? new Date(settings.nextDueDate) : null
  };
}

function resolveSubscriptionMembershipCreditPreview({
  settings = {},
  setup = {},
}) {
  const currency = String(setup?.currency || settings?.currency || 'GBP').toUpperCase();
  const interval = settings?.interval === 'month' ? 'month' : 'year';
  const intervalCount = Math.max(1, Number(settings?.intervalCount || setup?.fixedInterval?.count || 1));
  const cadenceMode = setup?.cadenceMode || 'fixed';
  const discountPct = cadenceMode === 'member_choice'
    ? Number(interval === 'month' ? setup?.memberChoice?.monthly?.discountPct || 0 : setup?.memberChoice?.yearly?.discountPct || 0)
    : 0;
  const membershipPlanType = normalizeMembershipPlanType(settings?.membershipPlanType);
  const memberCount = membershipPlanType === 'family_tier'
    ? Math.max(1, normalizeStringList(settings?.membershipCoveredMemberIds).length)
    : 1;
  const appliedTags = membershipPlanType === 'family_tier'
    ? []
    : normalizeStringList([
        ...(settings?.membershipPricingTag ? [settings.membershipPricingTag] : []),
        ...normalizeStringList(settings?.membershipAdminTagNames)
      ]);
  const familyTiers = Array.isArray(setup?.familyTiers) ? setup.familyTiers : [];
  const selectedTier = familyTiers.find(tier => {
    const tierId = typeof tier?.id === 'string' ? tier.id.trim() : '';
    return tierId && tierId === settings?.membershipPlanTierId;
  }) || null;
  const pricing = resolveCycleBasePricing({
    baseRate: membershipPlanType === 'family_tier' ? Number(selectedTier?.baseRate || 0) : Number(setup?.baseRate || 0),
    baseRateYearly: membershipPlanType === 'family_tier'
      ? (selectedTier?.baseRateYearly ?? (Number(selectedTier?.baseRate || 0) * 12))
      : setup?.baseRateYearly,
    tagOverrides: membershipPlanType === 'family_tier' ? {} : (setup?.tagOverrides || {}),
    tagOverridesYearly: membershipPlanType === 'family_tier' ? {} : (setup?.tagOverridesYearly || {}),
    appliedTags,
    preferredTag: membershipPlanType === 'family_tier' ? '' : (settings?.membershipPricingTag || ''),
    cadenceUnit: interval,
    cadenceCount: intervalCount
  });
  const effectivePlatformFeeMinor = Number.isFinite(Number(setup?.platformFeeMinor))
    ? Number(setup.platformFeeMinor) * (membershipPlanType === 'family_tier' ? memberCount : 1)
    : getPlatformFeeMinor({
        currency,
        teamFeeCfg: setup,
        memberNestFeePence: Number.isFinite(Number(setup?.memberNestFeePence)) ? Number(setup.memberNestFeePence) : undefined
      }) * (membershipPlanType === 'family_tier' ? memberCount : 1);
  const originalBreakdown = calculateTotalFees({
    baseAmount: Math.round(Number(pricing?.baseForCycle || 0)),
    cadence: {
      unit: interval,
      count: intervalCount,
      discountPct
    },
    platformFeeHandling: setup?.platformFeeHandling || 'deduct',
    stripeFeeHandling: setup?.stripeFeeHandling || 'deduct',
    currency,
    platformFeeMinor: effectivePlatformFeeMinor
  });
  const availableCreditMinor = Math.max(0, Number(settings?.membershipDiscountState?.creditBalanceMinor || 0));
  const creditEligibleGrossMinor = Math.max(
    0,
    Number(originalBreakdown?.finalAmount || 0) - Math.max(0, Number(originalBreakdown?.stripeFee || 0))
  );
  const creditAppliedMinor = Math.min(
    availableCreditMinor,
    Math.max(0, Number(originalBreakdown?.finalAmount || 0)),
    creditEligibleGrossMinor
  );
  if (creditAppliedMinor <= 0) return null;

  return {
    amountMinor: -creditAppliedMinor,
    currency,
    dueDate: typeof settings?.membershipYearAnchorTarget?.toDate === 'function'
      ? settings.membershipYearAnchorTarget.toDate()
      : typeof settings?.nextDueDate?.toDate === 'function'
        ? settings.nextDueDate.toDate()
        : settings?.membershipYearAnchorTarget || settings?.nextDueDate
          ? new Date(settings?.membershipYearAnchorTarget || settings?.nextDueDate)
          : null,
    source: 'team_settings_subscription_credit'
  };
}

export default function MySubscriptions() {
  const {
    user
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [portalError, setPortalError] = useState(null);
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [manualPayments, setManualPayments] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [renewalsLoading, setRenewalsLoading] = useState(true);
  const [loadedPaymentsUserId, setLoadedPaymentsUserId] = useState('');
  const [portalCurrencies, setPortalCurrencies] = useState([]);
  const [paymentsView, setPaymentsView] = useState('upcoming');
  const onlineRefreshSignal = useOnlineRefreshSignal();
  useEffect(() => {
    if (!user?.uid) {
      setSubscriptionCount(0);
      setManualPayments([]);
      setRenewals([]);
      setPaymentHistory([]);
      setPortalCurrencies([]);
      setError(null);
      setPortalError(null);
      setLoadedPaymentsUserId('');
      setRenewalsLoading(false);
      return;
    }
    const load = async () => {
      setRenewalsLoading(true);
      setError(null);
      setPortalError(null);
      try {
        // 1) Active Stripe subscriptions (cross-check against teamSettings to avoid stale docs)
        const [subSnap, settingsSnap, userSnap] = await Promise.all([getDocs(collection(db, `users/${user.uid}/subscriptions`)), getDocs(collection(db, `users/${user.uid}/teamSettings`)), getDoc(doc(db, `users/${user.uid}`))]);
        const activeLikeStatuses = new Set(['active', 'trialing', 'past_due', 'unpaid', 'paused']);
        const inactiveStatuses = new Set(['canceled', 'cancelled', 'none', 'incomplete_expired']);
        const settingsByTeamId = new Map(settingsSnap.docs.map(d => [d.id, d.data() || {}]));
        const activeSubscriptionTeamIds = Array.from(new Set(settingsSnap.docs.filter(d => {
          const s = d.data() || {};
          const modeOk = s.paymentMode === 'subscription';
          const status = String(s.status || '').toLowerCase();
          const statusOk = status ? !inactiveStatuses.has(status) : true;
          return modeOk && statusOk;
        }).map(d => d.id).filter(Boolean)));
        const isSettingsActive = teamId => {
          const s = settingsByTeamId.get(teamId);
          if (!s) return null; // unknown
          const modeOk = s.paymentMode === 'subscription';
          const status = String(s.status || '').toLowerCase();
          const statusOk = status ? !inactiveStatuses.has(status) : true;
          return modeOk && statusOk;
        };
        const activeSubscriptions = subSnap.docs.filter(d => {
          const data = d.data() || {};
          const status = String(data.status || '').toLowerCase();
          if (status && !activeLikeStatuses.has(status)) return false;
          const isBoltOnSubscription = Boolean(data?.boltOnId) || String(d.id).includes('__');
          if (isBoltOnSubscription) return true;

          // Sub doc ids can be "teamId" or "teamId__boltOnId"
          const teamId = data.teamId || String(d.id).split('__')[0];
          const settingsActive = isSettingsActive(teamId);

          // If we have authoritative settings, respect them; otherwise fall back to sub doc
          return settingsActive === null ? true : settingsActive;
        });
        setSubscriptionCount(activeSubscriptions.length);
        const userData = userSnap.exists() ? userSnap.data() || {} : {};
        const customerMap = userData?.stripeCustomerIdsByCurrency && typeof userData.stripeCustomerIdsByCurrency === 'object' && !Array.isArray(userData.stripeCustomerIdsByCurrency) ? userData.stripeCustomerIdsByCurrency : {};
        const detectedCurrencies = Object.entries(customerMap).filter(([, customerId]) => typeof customerId === 'string' && customerId.trim()).map(([currency]) => String(currency).trim().toUpperCase()).filter(Boolean).sort((a, b) => a.localeCompare(b));
        setPortalCurrencies(detectedCurrencies);

        // 2) Unified payment ledger read (manual + subscription)
        const paymentSnap = await getDocs(collection(db, `users/${user.uid}/payments`));
        const paymentRows = paymentSnap.docs.map(d => ({
          id: d.id,
          ...(d.data() || {})
        }));
        const isUnpaid = payment => payment?.paid === false || !payment?.paidAt;
        const isPaid = payment => payment?.paid === true || Boolean(payment?.paidAt);
        const amountFromPayment = payment => {
          const parsed = Number(payment?.finalAmount ?? payment?.amount);
          return Number.isFinite(parsed) ? parsed : null;
        };
        const isSupportedLedgerType = payment => {
          const paymentType = String(payment?.paymentType || '').toLowerCase();
          return !paymentType || paymentType === 'membership' || paymentType === 'bolt_on' || paymentType === 'membership_change';
        };
        const unpaidManual = paymentRows.filter(payment => {
          const method = String(payment?.method || '').toLowerCase();
          return method === 'manual' && isUnpaid(payment);
        });
        const subscriptionPaymentRows = paymentRows.filter(payment => {
          const method = String(payment?.method || '').toLowerCase();
          const paymentType = String(payment?.paymentType || '').toLowerCase();
          const isSupportedType = !paymentType || paymentType === 'membership' || paymentType === 'bolt_on';
          return method === 'subscription' && isSupportedType;
        });
        const paidHistoryRows = paymentRows.filter(payment => {
          const method = String(payment?.method || '').toLowerCase();
          const amountMinor = amountFromPayment(payment);
          return (
            (method === 'manual' || method === 'subscription' || method === 'stripe')
            && isSupportedLedgerType(payment)
            && isPaid(payment)
            && payment?.projected !== true
            && Number.isFinite(amountMinor)
            && amountMinor > 0
          );
        });
        const activeSubscriptionEntityMap = new Map();
        activeSubscriptions.forEach(d => {
          const data = d.data() || {};
          const idParts = String(d.id || '').split('__');
          const teamId = data.teamId || idParts[0] || '';
          const boltOnId = data.boltOnId || (idParts.length > 1 ? idParts.slice(1).join('__') : null);
          if (!teamId) return;
          const key = boltOnId ? `bolt_on:${teamId}:${boltOnId}` : `membership:${teamId}`;
          if (!activeSubscriptionEntityMap.has(key)) {
            activeSubscriptionEntityMap.set(key, {
              key,
              teamId,
              boltOnId: boltOnId || null,
              paymentType: boltOnId ? 'bolt_on' : 'membership'
            });
          }
        });
        // Membership subscriptions should also be discoverable from team settings even when
        // users/{uid}/subscriptions is missing/stale for that team.
        activeSubscriptionTeamIds.forEach(teamId => {
          if (!teamId) return;
          const key = `membership:${teamId}`;
          if (!activeSubscriptionEntityMap.has(key)) {
            activeSubscriptionEntityMap.set(key, {
              key,
              teamId,
              boltOnId: null,
              paymentType: 'membership'
            });
          }
        });
        const activeSubscriptionEntities = Array.from(activeSubscriptionEntityMap.values());
        const getJSDate = ts => typeof ts?.toDate === 'function' ? ts.toDate() : ts ? new Date(ts) : null;
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const toDate = value => {
          const date = getJSDate(value);
          return date && Number.isFinite(date.getTime()) ? date : null;
        };
        const dateKey = value => {
          const date = toDate(value);
          return date ? date.toISOString().slice(0, 10) : '';
        };
        const billingCycleFromDate = value => {
          const date = toDate(value);
          if (!date) return '';
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        };
        const normalizePaymentType = value => {
          const normalized = String(value || '').toLowerCase();
          return normalized || 'membership';
        };
        const normalizeBoltOnId = value => String(value || '').trim();
        const sameSubscriptionEntity = (payment, renewal) => {
          if (!payment?.teamId || payment.teamId !== renewal?.teamId) return false;
          if (normalizePaymentType(payment?.paymentType) !== normalizePaymentType(renewal?.paymentType)) return false;
          return normalizeBoltOnId(payment?.boltOnId) === normalizeBoltOnId(renewal?.boltOnId);
        };
        const paidSubscriptionRows = paidHistoryRows.filter(payment => {
          const method = String(payment?.method || '').toLowerCase();
          return method === 'subscription';
        });
        const paidManualMembershipRows = paymentRows.filter(payment => {
          const method = String(payment?.method || '').toLowerCase();
          const paymentType = String(payment?.paymentType || '').toLowerCase();
          return method === 'manual'
            && (!paymentType || paymentType === 'membership')
            && isPaid(payment)
            && payment?.projected !== true
            && payment?.teamId;
        });
        const isAlreadyPaidSubscriptionCycle = renewal => {
          if (renewal?.kind !== 'subscription') return false;
          if (renewal?.isProjectedRenewal !== true) return false;
          return paidSubscriptionRows.some(payment => {
            if (!sameSubscriptionEntity(payment, renewal)) return false;
            if (renewal?.billingCycle && payment?.billingCycle && renewal.billingCycle === payment.billingCycle) return true;

            const renewalStart = dateKey(renewal?.periodStart);
            const renewalEnd = dateKey(renewal?.periodEnd);
            const paymentStart = dateKey(payment?.periodStart);
            const paymentEnd = dateKey(payment?.periodEnd);
            if (renewalStart && paymentStart && renewalStart === paymentStart) return true;
            if (renewalStart && renewalEnd && paymentStart && paymentEnd && renewalStart === paymentStart && renewalEnd === paymentEnd) return true;

            const renewalDue = dateKey(renewal?.dueDate);
            const paymentDue = dateKey(payment?.dueDate);
            if (renewalDue && paymentDue && renewalDue === paymentDue) return true;
            if (renewalDue && paymentStart && renewalDue === paymentStart) return true;
            return false;
          });
        };
        const isAlreadyPaidManualMembershipCycle = renewal => {
          if (renewal?.kind !== 'manual' || renewal?.manualPayment || !renewal?.teamId) return false;
          const renewalCycle = String(renewal?.billingCycle || '').trim()
            || billingCycleFromDate(renewal?.dueDate);
          const renewalDue = dateKey(renewal?.dueDate);

          return paidManualMembershipRows.some(payment => {
            if (payment?.teamId !== renewal.teamId) return false;

            const paymentCycle = String(payment?.billingCycle || '').trim();
            if (renewalCycle && paymentCycle && renewalCycle === paymentCycle) return true;

            const paymentDue = dateKey(payment?.dueDate);
            const paymentStart = dateKey(payment?.periodStart);
            if (renewalDue && paymentDue && renewalDue === paymentDue) return true;
            if (renewalDue && paymentStart && renewalDue === paymentStart) return true;

            const hasCycleOrPeriod = Boolean(paymentCycle || paymentDue || paymentStart);
            const paymentPaidAt = dateKey(payment?.paidAt);
            return renewalDue && !hasCycleOrPeriod && paymentPaidAt && renewalDue === paymentPaidAt;
          });
        };
        const getPendingRenewalPreview = settings => {
          const billingTransition = settings?.pendingBillingTransition;
          const membershipChange = settings?.pendingMembershipChange;
          const pending = ['preparing', 'queued', 'confirmed'].includes(billingTransition?.status)
            ? billingTransition
            : membershipChange?.status === 'scheduled_for_renewal'
              ? membershipChange
              : null;
          if (!pending) return null;
          const amountMinor = Number(pending?.targetAmountMinor ?? pending?.nextRenewalAmountMinor);
          const cadenceTransition = getMembershipCadenceTransition(pending);
          return {
            amountMinor: Number.isFinite(amountMinor) ? amountMinor : null,
            currency: String(pending?.currency || settings?.currency || '').toUpperCase() || null,
            dueDate: toDate(pending?.effectiveAt) || toDate(settings?.nextDueDate) || null,
            currentCadence: cadenceTransition.currentCadence,
            targetCadence: cadenceTransition.targetCadence,
            cadenceChanged: cadenceTransition.changed,
            cadenceConfirmed: cadenceTransition.confirmedWithStripe || cadenceTransition.confirmedByClub,
            cadencePreparing: cadenceTransition.preparing,
            confirmationSource: pending?.confirmationSource || (cadenceTransition.confirmedWithStripe ? 'stripe' : ''),
            policyChange: pending?.source === 'club_policy'
          };
        };
        const getStoredManualUpcomingPreview = settings => {
          const preview = settings?.manualUpcomingPreview;
          const amountMinor = Number(preview?.amountMinor);
          if (!Number.isFinite(amountMinor)) return null;
          return {
            amountMinor,
            currency: String(preview?.currency || settings?.currency || '').toUpperCase() || null,
            dueDate: toDate(preview?.dueDate) || toDate(settings?.nextDueDate) || null,
            targetCadence: preview?.cadence || null,
            policyChange: preview?.source === 'club_policy',
            confirmationSource: preview?.source === 'club_policy' ? 'club' : ''
          };
        };
        const stripeRenewalByKey = new Map();
        if (activeSubscriptionEntities.length > 0) {
          try {
            const token = await user.getIdToken();
            const res = await fetch(getFunctionUrl('getStripeRenewals'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({})
            });
            const result = await res.json();
            if (res.ok && Array.isArray(result?.renewals)) {
              result.renewals.forEach(entry => {
                const key = String(entry?.key || '');
                if (!key) return;
                stripeRenewalByKey.set(key, {
                  dueDate: toDate(entry?.dueDate),
                  status: String(entry?.status || '').toLowerCase(),
                  amountMinor: entry?.amountMinor != null && Number.isFinite(Number(entry.amountMinor)) ? Number(entry.amountMinor) : null,
                  currency: String(entry?.currency || '').toUpperCase() || null,
                  dueDateSource: String(entry?.dueDateSource || '')
                });
              });
            }
          } catch (stripeLookupError) {}
        }

        // 3) Enrich names for teams and bolt-ons across all sources
        const teamIdsNeedingNames = Array.from(new Set([...unpaidManual.filter(payment => payment?.teamId && !payment?.teamName).map(payment => payment.teamId), ...subscriptionPaymentRows.filter(payment => payment?.teamId && !payment?.teamName).map(payment => payment.teamId), ...paidHistoryRows.filter(payment => payment?.teamId && !payment?.teamName).map(payment => payment.teamId), ...activeSubscriptionEntities.map(entity => entity.teamId), ...activeSubscriptionTeamIds, ...settingsSnap.docs.filter(d => {
          const s = d.data() || {};
          const mode = String(s.paymentMode || '').toLowerCase();
          const status = String(s.status || '').toLowerCase();
          const modeOk = mode === 'manual' || mode === 'subscription';
          const statusOk = status ? !inactiveStatuses.has(status) : true;
          return modeOk && statusOk;
        }).map(d => d.id)]));
        const teamNameById = {};
        const teamSetupById = {};
        await Promise.all(teamIdsNeedingNames.map(async teamId => {
          try {
            const [tSnap, setupSnap] = await Promise.all([
              getDoc(doc(db, `teams/${teamId}`)),
              getDoc(doc(db, `teams/${teamId}/stripe/setup`)).catch(() => null),
            ]);
            if (tSnap.exists()) teamNameById[teamId] = tSnap.data()?.name || '';
            if (setupSnap?.exists) teamSetupById[teamId] = setupSnap.data() || {};
          } catch {
            // ignore
          }
        }));
        const boltOnKeys = Array.from(new Set([...unpaidManual.filter(payment => String(payment?.paymentType || '').toLowerCase() === 'bolt_on' && payment?.teamId && payment?.boltOnId).map(payment => `${payment.teamId}__${payment.boltOnId}`), ...subscriptionPaymentRows.filter(payment => String(payment?.paymentType || '').toLowerCase() === 'bolt_on' && payment?.teamId && payment?.boltOnId).map(payment => `${payment.teamId}__${payment.boltOnId}`), ...paidHistoryRows.filter(payment => String(payment?.paymentType || '').toLowerCase() === 'bolt_on' && payment?.teamId && payment?.boltOnId).map(payment => `${payment.teamId}__${payment.boltOnId}`), ...activeSubscriptionEntities.filter(entity => entity?.boltOnId).map(entity => `${entity.teamId}__${entity.boltOnId}`)]));
        const boltOnNameByKey = {};
        const boltOnEntryByKey = {};
        await Promise.all(boltOnKeys.map(async key => {
          const [teamId, boltOnId] = key.split('__');
          if (!teamId || !boltOnId) return;
          try {
            const [boSnap, entrySnap] = await Promise.all([
              getDoc(doc(db, `teams/${teamId}/boltOns/${boltOnId}`)),
              getDoc(doc(db, `teams/${teamId}/members/${user.uid}/boltOns/${boltOnId}`)).catch(() => null),
            ]);
            if (entrySnap?.exists()) boltOnEntryByKey[key] = entrySnap.data();
            boltOnNameByKey[key] = boSnap.exists() ? boSnap.data()?.name || 'Bolt‑On' : 'Bolt‑On';
          } catch {
            boltOnNameByKey[key] = 'Bolt‑On';
          }
        }));
        const paymentEntityKey = payment => {
          const teamId = payment?.teamId;
          if (!teamId) return null;
          const paymentType = String(payment?.paymentType || '').toLowerCase();
          if (paymentType === 'bolt_on' && payment?.boltOnId) return `bolt_on:${teamId}:${payment.boltOnId}`;
          return `membership:${teamId}`;
        };
        const paymentRowsByEntity = new Map();
        subscriptionPaymentRows.forEach(payment => {
          const key = paymentEntityKey(payment);
          if (!key) return;
          const rows = paymentRowsByEntity.get(key) || [];
          rows.push(payment);
          paymentRowsByEntity.set(key, rows);
        });
        const pickAmountMinorFromRows = rows => {
          if (!Array.isArray(rows) || rows.length === 0) return 0;
          const withMarker = rows.map(payment => ({
            amount: amountFromPayment(payment),
            marker: toDate(payment?.periodEnd) || toDate(payment?.paidAt) || toDate(payment?.createdAt) || toDate(payment?.dueDate)
          })).filter(row => Number.isFinite(row.amount)).sort((a, b) => {
            const am = a?.marker?.getTime?.() ?? Number.NEGATIVE_INFINITY;
            const bm = b?.marker?.getTime?.() ?? Number.NEGATIVE_INFINITY;
            return bm - am;
          });
          if (withMarker.length === 0) return 0;
          const recentPositive = withMarker.find(row => Number(row.amount) > 0);
          if (recentPositive) return Number(recentPositive.amount);
          return Number(withMarker[0].amount || 0);
        };
        const teamCycleIdsCache = new Map();
        const getTeamCycleIdsDesc = async teamId => {
          if (!teamId) return [];
          if (teamCycleIdsCache.has(teamId)) return teamCycleIdsCache.get(teamId);
          try {
            const cycleSnap = await getDocs(collection(db, `teams/${teamId}/billingCycles`));
            const ids = cycleSnap.docs.map(d => String(d.id || '')).filter(Boolean).sort((a, b) => b.localeCompare(a));
            teamCycleIdsCache.set(teamId, ids);
            return ids;
          } catch {
            teamCycleIdsCache.set(teamId, []);
            return [];
          }
        };
        const memberBillingDocCache = new Map();
        const getMemberBillingDoc = async (teamId, cycleId, memberDocId) => {
          const key = `${teamId}::${cycleId}::${memberDocId}`;
          if (memberBillingDocCache.has(key)) return memberBillingDocCache.get(key);
          try {
            const memberSnap = await getDoc(doc(db, `teams/${teamId}/billingCycles/${cycleId}/members/${memberDocId}`));
            const data = memberSnap.exists() ? memberSnap.data() || null : null;
            memberBillingDocCache.set(key, data);
            return data;
          } catch {
            memberBillingDocCache.set(key, null);
            return null;
          }
        };
        const getDueDateFromTeamBillingCycles = async ({
          teamId,
          boltOnId
        }) => {
          const cycleIds = await getTeamCycleIdsDesc(teamId);
          if (!cycleIds.length) return null;
          const memberDocIds = boltOnId ? [`${user.uid}__bo__${boltOnId}`, user.uid] : [user.uid];
          for (const cycleId of cycleIds) {
            for (const memberDocId of memberDocIds) {
              const row = await getMemberBillingDoc(teamId, cycleId, memberDocId);
              if (!row) continue;
              const paymentType = String(row?.paymentType || '').toLowerCase();
              if (boltOnId && paymentType && paymentType !== 'bolt_on') continue;
              if (!boltOnId && paymentType === 'bolt_on') continue;
              const endDate = toDate(row?.periodEnd) || toDate(row?.dueDate) || toDate(row?.periodStart) || toDate(row?.paidAt) || toDate(row?.createdAt);
              if (endDate) return endDate;
            }
          }
          return null;
        };
        const pickSubscriptionRow = rows => {
          if (!Array.isArray(rows) || rows.length === 0) return null;
          const byDateAsc = (a, b) => {
            const ad = a?.dueDate?.getTime?.() ?? Number.POSITIVE_INFINITY;
            const bd = b?.dueDate?.getTime?.() ?? Number.POSITIVE_INFINITY;
            return ad - bd;
          };
          const projected = rows.map(payment => {
            if (!isUnpaid(payment)) return null;
            const projectionLike = payment?.projected === true || payment?.projectionSource === 'processSubscriptionProjections' || String(payment?.id || '').startsWith('membership__subscription__');
            if (!projectionLike) return null;
            const dueDate = toDate(payment?.dueDate) || toDate(payment?.periodStart) || toDate(payment?.periodEnd);
            if (!dueDate || dueDate < startOfToday) return null;
            return {
              payment,
              dueDate,
              source: 'projected'
            };
          }).filter(Boolean).sort(byDateAsc)[0];
          if (projected) return projected;
          const paidPeriodEnd = rows.map(payment => {
            if (isUnpaid(payment)) return null;
            const dueDate = toDate(payment?.periodEnd);
            if (!dueDate || dueDate < startOfToday) return null;
            return {
              payment,
              dueDate,
              source: 'paid_period_end'
            };
          }).filter(Boolean).sort(byDateAsc)[0];
          if (paidPeriodEnd) return paidPeriodEnd;
          const latest = rows.map(payment => {
            const marker = toDate(payment?.periodEnd) || toDate(payment?.paidAt) || toDate(payment?.createdAt);
            return {
              payment,
              marker
            };
          }).sort((a, b) => {
            const am = a?.marker?.getTime?.() ?? Number.NEGATIVE_INFINITY;
            const bm = b?.marker?.getTime?.() ?? Number.NEGATIVE_INFINITY;
            return bm - am;
          })[0];
          if (!latest?.payment) return null;
          return {
            payment: latest.payment,
            dueDate: null,
            source: 'history_fallback'
          };
        };
        const subscriptionRenewalRowsBase = activeSubscriptionEntities.map(entity => {
          const matchedRows = paymentRowsByEntity.get(entity.key) || [];
          const picked = pickSubscriptionRow(matchedRows);
          const payment = picked?.payment || {};
          const teamSettings = settingsByTeamId.get(entity.teamId) || {};
          const boltOnEntry = entity?.boltOnId ? boltOnEntryByKey[`${entity.teamId}__${entity.boltOnId}`] : null;
          const pendingBoltOnTransition = boltOnEntry?.renewalTransition?.status === 'scheduled' ? boltOnEntry.renewalTransition : null;
          const pendingRenewal = !entity?.boltOnId ? getPendingRenewalPreview(teamSettings) : null;
          const stripeRow = stripeRenewalByKey.get(entity.key) || null;
          const stripeDueDate = stripeRow?.dueDate || null;
          const anchorDueDate = !entity?.boltOnId ? toDate(teamSettings?.membershipYearAnchorTarget) || toDate(teamSettings?.nextDueDate) : null;
          const teamName = payment?.teamName || teamNameById[entity.teamId] || 'Team';
          const boltOnKey = entity?.boltOnId ? `${entity.teamId}__${entity.boltOnId}` : '';
          const boltOnName = entity?.boltOnId ? boltOnNameByKey[boltOnKey] || 'Bolt‑On' : '';
          const displayName = entity?.boltOnId ? `${teamName} · ${boltOnName}` : teamName;
          const fallbackAmountMinor = Number(teamSettings?.migrationRenewalAmountMinor) > 0 ? Number(teamSettings.migrationRenewalAmountMinor) : pickAmountMinorFromRows(matchedRows);
          const projectedAmountMinor = Number.isFinite(Number(payment?.finalAmount ?? payment?.amount))
            && picked?.source === 'projected'
            ? Number(payment?.finalAmount ?? payment?.amount)
            : null;
          const projectedStoredCreditAppliedMinor = Number.isFinite(
            Number(
              payment?.storedCreditAppliedMinor
              ?? payment?.breakdown?.storedCreditAppliedMinor
            )
          )
            ? Number(
                payment?.storedCreditAppliedMinor
                ?? payment?.breakdown?.storedCreditAppliedMinor
              )
            : 0;
          const teamCreditBalanceMinor = Number(
            teamSettings?.membershipDiscountState?.creditBalanceMinor || 0
          );
          const shouldPreferProjectedAmount = Number.isFinite(projectedAmountMinor) && (
            Number(projectedAmountMinor) > 0
            || (
              Number(teamCreditBalanceMinor) > 0
              && picked?.source === 'projected'
            )
          );
          const stripeAmountMinor = stripeRow?.amountMinor != null && Number.isFinite(Number(stripeRow.amountMinor))
            ? Number(stripeRow.amountMinor)
            : null;
          const baseUpcomingAmountMinor = Number(
            pendingRenewal?.amountMinor != null && Number.isFinite(Number(pendingRenewal.amountMinor))
              ? Number(pendingRenewal.amountMinor)
              : shouldPreferProjectedAmount
                ? Number(projectedAmountMinor)
                : Number.isFinite(projectedAmountMinor)
                  && !Number.isFinite(stripeAmountMinor)
                ? Number(projectedAmountMinor)
                : Number.isFinite(stripeAmountMinor)
                ? Number(stripeAmountMinor)
                : fallbackAmountMinor
          );
          const creditPreview = !entity?.boltOnId && !pendingRenewal
            ? resolveSubscriptionMembershipCreditPreview({
                settings: teamSettings,
                setup: teamSetupById[entity.teamId] || {}
              })
            : null;
          const baselineAmountMinor = Number.isFinite(stripeAmountMinor)
            ? Number(stripeAmountMinor)
            : baseUpcomingAmountMinor;
          const projectedAlreadyIncludesCredit = picked?.source === 'projected'
            && (
              projectedStoredCreditAppliedMinor > 0
              || (
                Number.isFinite(projectedAmountMinor)
                && Number(projectedAmountMinor) < Number(baselineAmountMinor)
              )
            );
          const shouldApplyCreditToDisplay = Boolean(
            creditPreview
            && Number.isFinite(Number(creditPreview.amountMinor))
            && Number(creditPreview.amountMinor) < 0
            && !projectedAlreadyIncludesCredit
          );
          return {
            id: `subscription:${entity.key}`,
            boltOnEntry: boltOnEntryByKey[boltOnKey] || null,
            kind: 'subscription',
            teamId: entity.teamId,
            paymentType: entity.paymentType,
            teamName: displayName,
            amountMinor: pendingBoltOnTransition ? Number(pendingBoltOnTransition.bridgeChargeAmount) : shouldApplyCreditToDisplay
              ? Math.max(0, baseUpcomingAmountMinor + Number(creditPreview.amountMinor))
              : baseUpcomingAmountMinor,
            currency: pendingRenewal?.currency || stripeRow?.currency || payment?.currency || 'GBP',
            dueDate: toDate(pendingBoltOnTransition?.bridgeStart) || pendingRenewal?.dueDate || stripeDueDate || anchorDueDate || picked?.dueDate || null,
            periodStart: picked?.source === 'projected' ? toDate(payment?.periodStart) || null : null,
            periodEnd: picked?.source === 'projected' ? toDate(payment?.periodEnd) || null : null,
            billingCycle: picked?.source === 'projected' ? payment?.billingCycle || '' : '',
            source: pendingRenewal
              ? 'pending_membership_change'
              : shouldApplyCreditToDisplay
                ? 'subscription_with_credit_applied'
              : stripeDueDate
                ? 'stripe_current_period_end'
                : anchorDueDate
                  ? 'team_settings_anchor'
                  : picked?.source || 'active_subscription',
            boltOnId: entity?.boltOnId || null,
            isProjectedRenewal: picked?.source === 'projected',
            dueDateSource: stripeRow?.dueDateSource || '',
            creditAppliedMinor: shouldApplyCreditToDisplay
              ? Math.abs(Number(creditPreview.amountMinor || 0))
              : 0,
            currentCadence: pendingRenewal?.currentCadence || null,
            targetCadence: pendingRenewal?.targetCadence || null,
            cadenceChanged: pendingRenewal?.cadenceChanged === true,
            cadenceConfirmed: pendingRenewal?.cadenceConfirmed === true,
            cadencePreparing: pendingRenewal?.cadencePreparing === true,
            confirmationSource: pendingRenewal?.confirmationSource || '',
            policyChange: pendingRenewal?.policyChange === true,
          };
        });
        const unresolvedRows = subscriptionRenewalRowsBase.filter(row => !row?.dueDate && row?.teamId);
        const resolvedRows = await Promise.all(unresolvedRows.map(async row => {
          const dueDate = await getDueDateFromTeamBillingCycles({
            teamId: row.teamId,
            boltOnId: row.boltOnId || null
          });
          return {
            id: row.id,
            dueDate
          };
        }));
        const resolvedById = new Map(resolvedRows.map(row => [row.id, row.dueDate || null]));
        const subscriptionRenewalRows = subscriptionRenewalRowsBase.map(row => ({
          ...row,
          dueDate: row.dueDate || resolvedById.get(row.id) || null
        })).filter(row => !isAlreadyPaidSubscriptionCycle(row));
        const enrichedManualPayments = unpaidManual.map(payment => {
          const teamName = payment?.teamName || teamNameById[payment?.teamId] || 'Team';
          const paymentType = String(payment?.paymentType || '').toLowerCase();
          const boltOnKey = payment?.teamId && payment?.boltOnId ? `${payment.teamId}__${payment.boltOnId}` : '';
          const boltOnName = paymentType === 'bolt_on' ? boltOnNameByKey[boltOnKey] || 'Bolt‑On' : '';
          return {
            ...payment,
            teamName,
            boltOnName,
            boltOnEntry: boltOnEntryByKey[boltOnKey] || null,
          };
        });
        enrichedManualPayments.sort((a, b) => {
          const ad = toDate(a?.dueDate) || toDate(a?.createdAt) || new Date(8640000000000000);
          const bd = toDate(b?.dueDate) || toDate(b?.createdAt) || new Date(8640000000000000);
          return ad - bd;
        });
        setManualPayments(enrichedManualPayments);
        const manualMembershipRenewalRows = enrichedManualPayments
          .filter(payment => {
            const paymentType = String(payment?.paymentType || '').toLowerCase();
            return paymentType === 'membership' && payment?.teamId;
          })
          .map(payment => ({
            id: `manual:payment:${payment.id}`,
            kind: 'manual',
            teamId: payment.teamId,
            teamName: `${payment.teamName || teamNameById[payment.teamId] || 'Team'} · Membership`,
            amountMinor: Number(payment?.finalAmount ?? payment?.amount ?? 0),
            currency: String(payment?.currency || 'GBP').toUpperCase(),
            dueDate: toDate(payment?.dueDate) || toDate(payment?.periodStart) || toDate(payment?.createdAt) || null,
            source: 'manual_payment_row',
            manualPayment: payment
          }));
        const manualMembershipTeamIds = new Set(manualMembershipRenewalRows.map(row => row.teamId).filter(Boolean));
        const enrichedPaymentHistory = paidHistoryRows.map(payment => {
          const paymentType = String(payment?.paymentType || '').toLowerCase();
          const teamName = payment?.teamName || teamNameById[payment?.teamId] || 'Team';
          const boltOnKey = payment?.teamId && payment?.boltOnId ? `${payment.teamId}__${payment.boltOnId}` : '';
          const boltOnName = paymentType === 'bolt_on' ? boltOnNameByKey[boltOnKey] || 'Bolt-On' : '';
          const paidAt = toDate(payment?.paidAt) || toDate(payment?.periodEnd) || toDate(payment?.createdAt) || null;
          return {
            ...payment,
            teamName,
            boltOnName,
            paidAtDate: paidAt
          };
        }).sort((a, b) => {
          const ad = a?.paidAtDate?.getTime?.() ?? Number.NEGATIVE_INFINITY;
          const bd = b?.paidAtDate?.getTime?.() ?? Number.NEGATIVE_INFINITY;
          if (ad !== bd) return bd - ad;
          return String(a?.teamName || '').localeCompare(String(b?.teamName || ''));
        });
        setPaymentHistory(enrichedPaymentHistory);
        const manualSettingsCandidates = settingsSnap.docs.map(d => ({
          teamId: d.id,
          settings: d.data() || {}
        })).filter(({
          settings
        }) => {
          const mode = String(settings?.paymentMode || '').toLowerCase();
          const status = String(settings?.status || '').toLowerCase();
          if (mode !== 'manual') return false;
          if (status && inactiveStatuses.has(status)) return false;
          return true;
        });
        const manualSettingsRows = (await Promise.all(manualSettingsCandidates.map(async ({
          teamId,
          settings
        }) => {
          if (manualMembershipTeamIds.has(teamId)) return null;
          const pendingRenewal = getPendingRenewalPreview(settings);
          const storedUpcomingPreview = getStoredManualUpcomingPreview(settings);
          let computedRenewal = pendingRenewal || storedUpcomingPreview;
          let source = pendingRenewal
            ? 'pending_membership_change'
            : (storedUpcomingPreview ? 'manual_upcoming_preview' : 'team_settings_manual_live');

          if (!computedRenewal) {
            computedRenewal = resolveManualMembershipRenewal({
              settings,
              setup: teamSetupById[teamId] || {},
              pendingRenewal: null
            });
            source = 'team_settings_manual_live';
          }

          const teamName = teamNameById[teamId] || 'Team';
          const row = {
            id: `manual:settings:${teamId}`,
            kind: 'manual',
            teamId,
            teamName: `${teamName} · Membership`,
            amountMinor: Number(computedRenewal?.amountMinor || 0),
            currency: computedRenewal?.currency || 'GBP',
            dueDate: computedRenewal?.dueDate || null,
            billingCycle: billingCycleFromDate(computedRenewal?.dueDate),
            source,
            manualPayment: null,
            currentCadence: computedRenewal?.currentCadence || null,
            targetCadence: computedRenewal?.targetCadence || null,
            cadenceChanged: computedRenewal?.cadenceChanged === true,
            cadenceConfirmed: computedRenewal?.cadenceConfirmed === true,
            cadencePreparing: computedRenewal?.cadencePreparing === true,
            confirmationSource: computedRenewal?.confirmationSource || '',
            policyChange: computedRenewal?.policyChange === true
          };
          return isAlreadyPaidManualMembershipCycle(row) ? null : row;
        }))).filter(Boolean);
        const combinedRenewals = [...subscriptionRenewalRows, ...manualMembershipRenewalRows, ...manualSettingsRows];
        combinedRenewals.sort((a, b) => {
          const ad = a?.dueDate?.getTime?.();
          const bd = b?.dueDate?.getTime?.();
          const hasAd = Number.isFinite(ad);
          const hasBd = Number.isFinite(bd);
          if (hasAd && hasBd && ad !== bd) return ad - bd;
          if (hasAd !== hasBd) return hasAd ? -1 : 1;
          if (a.kind !== b.kind) return a.kind === 'manual' ? -1 : 1;
          return String(a.teamName || '').localeCompare(String(b.teamName || ''));
        });
        setRenewals(combinedRenewals);
      } catch {
        setError('Could not load your subscriptions and payments.');
        setManualPayments([]);
        setRenewals([]);
        setPaymentHistory([]);
        setPortalCurrencies([]);
      } finally {
        setLoadedPaymentsUserId(user.uid);
        setRenewalsLoading(false);
      }
    };
    load();
  }, [onlineRefreshSignal, user, user?.uid]);
  const formatMoney = (minor, currency = 'GBP') => {
    const code = String(currency || 'GBP').toUpperCase();
    const {
      decimals
    } = getCurrencyMeta(code);
    const major = Number(minor || 0) / Math.pow(10, decimals);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(major);
    } catch {
      return `${major.toFixed(decimals)} ${code}`;
    }
  };
  const safeDate = ts => typeof ts?.toDate === 'function' ? ts.toDate() : ts ? new Date(ts) : null;
  const yyyymm = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const getCadenceLabel = payment => {
    const unit = payment?.cadenceUnit || payment?.cadence?.unit;
    if (unit === 'month') return 'Monthly';
    if (unit === 'year') return 'Annual';
    const start = safeDate(payment?.periodStart);
    const end = safeDate(payment?.periodEnd);
    if (!start || !end) return null;
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (months >= 12) return 'Annual';
    if (months >= 1) return 'Monthly';
    return null;
  };
  const getPaymentLabel = payment => {
    const paymentType = String(payment?.paymentType || '').toLowerCase();
    if (paymentType === 'bolt_on') return payment?.label || payment?.boltOnName || 'Bolt-On';
    if (paymentType === 'membership_change') return payment?.label || 'Membership Change';
    return payment?.label || 'Membership';
  };
  const getMethodLabel = payment => {
    const method = String(payment?.method || '').toLowerCase();
    if (method === 'subscription') return 'Subscription';
    if (method === 'stripe') return 'Stripe';
    return 'Manual';
  };
  const handleManualPayment = async payment => {
    if (!user) return;
    setLoading(true);
    setError(null);
    setPortalError(null);
    try {
      const paymentType = String(payment?.paymentType || '').toLowerCase();
      if (paymentType === 'bolt_on') {
        if (!payment?.teamId || !payment?.boltOnId) throw new Error('Missing teamId or boltOnId.');
        const createBoltOnCheckoutSession = httpsCallable(functions, 'createBoltOnCheckoutSession');
        const {
          data
        } = await createBoltOnCheckoutSession({
          teamId: payment.teamId,
          boltOnId: payment.boltOnId,
          quantity: Math.max(1, Number(payment?.quantity || 1))
        });
        if (!data?.url) throw new Error('No checkout URL returned.');
        window.location.href = data.url;
        return;
      }
      if (paymentType === 'membership_change') {
        if (!payment?.teamId) throw new Error('Missing team information for this payment.');
        const token = await user.getIdToken(true);
        const res = await fetch(getFunctionUrl('payPendingMembershipChange'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            teamId: payment.teamId
          })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error(data?.error || 'Could not initiate payment for this membership change.');
      }
      if (!payment?.teamId) throw new Error('Missing team information for this payment.');
      const billingCycle = payment?.billingCycle || (() => {
        const seed = safeDate(payment?.periodStart) || safeDate(payment?.dueDate) || safeDate(payment?.createdAt);
        return seed ? yyyymm(seed) : null;
      })();
      if (!billingCycle) throw new Error('Missing billing cycle for this payment.');
      const cadenceUnit = payment?.cadenceUnit || payment?.cadence?.unit || (() => {
        const start = safeDate(payment?.periodStart);
        const end = safeDate(payment?.periodEnd);
        if (!start || !end) return null;
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        if (months >= 12) return 'year';
        if (months >= 1) return 'month';
        return null;
      })();
      const cadenceCount = Number(payment?.cadenceIntervalCount) || Number(payment?.cadence?.intervalCount) || null;
      const token = await user.getIdToken(true);
      const res = await fetch(getFunctionUrl('createManualCheckoutSession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId: payment.teamId,
          userId: user.uid,
          memberId: user.uid,
          paymentType: 'membership',
          billingCycle,
          ...(payment?.id ? {
            manualPaymentId: payment.id,
            paymentRevision: Number(payment?.paymentRevision || 0)
          } : {}),
          ...(Array.isArray(payment?.membershipTargetUserIds) && payment.membershipTargetUserIds.length > 0 ? {
            membershipTargetUserIds: payment.membershipTargetUserIds
          } : {}),
          ...(payment?.membershipTargetFamilyId ? {
            membershipTargetFamilyId: payment.membershipTargetFamilyId
          } : {}),
          ...(payment?.membershipPlanType ? {
            membershipPlanType: payment.membershipPlanType
          } : {}),
          ...(payment?.membershipPlanTierId ? {
            membershipPlanTierId: payment.membershipPlanTierId
          } : {}),
          ...(Array.isArray(payment?.appliedTags) && payment.appliedTags.length > 0 ? {
            membershipAppliedTags: payment.appliedTags
          } : {}),
          ...(cadenceUnit ? {
            selectedCadence: {
              unit: cadenceUnit,
              ...(cadenceCount ? {
                count: cadenceCount
              } : {})
            }
          } : {})
        })
      });
      const data = await res.json();
      if (res.ok && data?.url) window.location.href = data.url;else throw new Error(data?.error || 'No checkout URL returned.');
    } catch (e) {
      setError(e?.message || 'Could not initiate manual payment.');
    } finally {
      setLoading(false);
    }
  };
  const handleManage = async (currency = '') => {
    if (!user) return;
    if (subscriptionCount <= 0) {
      setPortalError('You have no active recurring subscriptions to manage.');
      return;
    }
    setLoading(true);
    setPortalError(null);
    try {
      const token = await user.getIdToken(true);
      const requestedCurrency = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
      const res = await fetch(getFunctionUrl('createStripePortalSession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestedCurrency ? {
          currency: requestedCurrency
        } : {})
      });
      const result = await res.json();
      if (res.ok && result?.url) window.location.href = result.url;else throw new Error(result?.error || 'No URL returned');
    } catch (e) {
      setPortalError('Could not open Stripe portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const hasUpcomingPayments = renewals.length > 0;
  const hasPastPayments = paymentHistory.length > 0;
  const hasResolvedPayments = loadedPaymentsUserId === user?.uid;
  return <>
      {loading && <LoadingModal message="Fetching Stripe…" />}

      <Card title="Recurring Subscriptions" className="dashboard-card">
        <p style={{
        color: 'var(--clr-text-muted)'
      }}>
          You have {subscriptionCount} active recurring subscription{subscriptionCount === 1 ? '' : 's'} via Stripe.
        </p>
        <p style={{
        color: 'var(--clr-text-muted)',
        marginTop: '0.25rem'
      }}>
          {subscriptionCount > 0 ? 'Use the button below to view or cancel your active recurring payments.' : 'No active recurring subscriptions to manage.'}
        </p>
        {portalError && <p className="text-error" style={{
        marginTop: '0.5rem'
      }}>{portalError}</p>}
        {subscriptionCount > 0 && <div className="form-actions" style={{
        marginTop: '1rem'
      }}>
          {portalCurrencies.length > 1 ? portalCurrencies.map(currency => <button key={`portal-${currency}`} className="button" onClick={() => handleManage(currency)} disabled={loading}>
                {loading ? 'Loading…' : `Manage ${currency} subscriptions`}
              </button>) : portalCurrencies.length === 1 ? <button className="button" onClick={() => handleManage(portalCurrencies[0])} disabled={loading}>
              {loading ? 'Loading…' : `Manage ${portalCurrencies[0]} subscriptions`}
            </button> : <button className="button" onClick={() => handleManage()} disabled={loading}>
              {loading ? 'Loading…' : 'Manage Subscriptions'}
            </button>}
        </div>}
      </Card>

      <Card title="Payments" className="dashboard-card">
        <div style={{
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
        marginBottom: '0.75rem'
      }}>
          <button className={paymentsView === 'upcoming' ? 'button' : 'button button-secondary'} onClick={() => setPaymentsView('upcoming')} disabled={renewalsLoading}>
            Upcoming
          </button>
          <button className={paymentsView === 'past' ? 'button' : 'button button-secondary'} onClick={() => setPaymentsView('past')} disabled={renewalsLoading}>
            Past
          </button>
        </div>
        {renewalsLoading && !hasResolvedPayments ? <>
            <p style={{
          color: 'var(--clr-text-muted)'
        }}>
              Loading payments...
            </p>
            {error && <p className="text-error" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </> : paymentsView === 'upcoming' ? hasUpcomingPayments ? <>
            <p style={{
          color: 'var(--clr-text-muted)'
        }}>
              Upcoming payments and renewals for this account:
            </p>
            <div style={{
          marginTop: '0.5rem',
          maxHeight: '16rem',
          overflowY: 'auto',
          paddingRight: '0.25rem'
        }}>
              {renewals.map(renewal => {
            const dueDate = renewal?.dueDate || null;
            const modeLabel = renewal.kind === 'manual'
              ? 'Manual'
              : 'Subscription';
            const dueLabel = !dueDate ? 'date to be confirmed' : dueDate < todayStart ? `overdue since ${dueDate.toLocaleDateString('en-GB')}` : `due ${dueDate.toLocaleDateString('en-GB')}`;
            const targetCadenceLabel = cadenceFrequencyLabel(renewal?.targetCadence);
            const currentCadenceLabel = cadenceFrequencyLabel(renewal?.currentCadence);
            return <div key={renewal.id} style={{
              marginBottom: '0.35rem'
            }}>
                    <p>[{modeLabel}] {renewal.teamName} — {formatMoney(renewal.amountMinor, renewal.currency)}{targetCadenceLabel ? ` ${targetCadenceLabel}` : ''} — {dueLabel}
                    {renewal.cadenceChanged && targetCadenceLabel && <span className="subscription-cadence-change">
                        Billing frequency changes from {currentCadenceLabel || 'the current cadence'} to {targetCadenceLabel}{renewal.cadenceConfirmed ? ' (confirmed with Stripe)' : ''}.
                      </span>}
                    {renewal.policyChange && <span className="subscription-cadence-change">Club billing-policy change{renewal.cadencePreparing ? ' (being prepared)' : renewal.confirmationSource === 'club' ? ' (confirmed by club)' : renewal.confirmationSource === 'stripe' || renewal.confirmationSource === 'current_price' ? ' (confirmed with Stripe)' : ''}.</span>}
                    {renewal.kind === 'subscription' && Number(renewal?.creditAppliedMinor || 0) > 0 && ` (includes ${formatMoney(-Number(renewal.creditAppliedMinor || 0), renewal.currency)} credit)`}
                    {renewal.kind === 'manual' && renewal.manualPayment && <button style={{
                marginLeft: '0.5rem'
              }} className="button small" onClick={() => handleManualPayment(renewal.manualPayment)} disabled={loading}>
                        Pay Now
                      </button>}
                    </p>
                    {renewal.boltOnEntry && <BoltOnRenewalSummary entry={renewal.boltOnEntry} currency={renewal.currency} />}
                  </div>;
          })}
            </div>
            {error && <p className="text-error" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </> : <>
            <p style={{
          color: 'var(--clr-text-muted)'
        }}>
              No upcoming payments found.
            </p>
            {error && <p className="text-error" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </> : hasPastPayments ? <>
            <p style={{
          color: 'var(--clr-text-muted)'
        }}>
              Past payments made through Member Nest:
            </p>
            <ul style={{
          marginTop: '0.5rem',
          paddingLeft: '1rem',
          maxHeight: '16rem',
          overflowY: 'auto',
          paddingRight: '0.25rem'
        }}>
              {paymentHistory.map(payment => {
            const paidAt = payment?.paidAtDate;
            const cadenceLabel = String(payment?.paymentType || '').toLowerCase() === 'membership' ? getCadenceLabel(payment) : null;
            return <li key={payment.id} style={{
              marginBottom: '0.5rem'
            }}>
                    <strong>{getPaymentLabel(payment)}</strong>
                    {payment?.teamName && <span> · {payment.teamName}</span>}
                    {cadenceLabel && <span> · {cadenceLabel}</span>}
                    <span> · {getMethodLabel(payment)}</span>
                    {' — '}
                    {formatMoney(payment?.finalAmount ?? payment?.amount ?? 0, payment?.currency || 'GBP')}
                    {' — '}
                    {paidAt ? `paid ${paidAt.toLocaleDateString('en-GB')}` : 'paid'}
                  </li>;
          })}
            </ul>
            {error && <p className="text-error" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </> : <>
            <p style={{
          color: 'var(--clr-text-muted)'
        }}>
              No past payments found.
            </p>
            {error && <p className="text-error" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </>}
      </Card>

      <Card title="Manual Payments" className="dashboard-card">
        {manualPayments.length > 0 ? <>
            <p style={{
          color: 'var(--clr-text-muted)'
        }}>
              You have {manualPayments.length} outstanding manual payment{manualPayments.length === 1 ? '' : 's'}.
            </p>
            <ul style={{
          marginTop: '0.5rem',
          paddingLeft: '1rem',
          maxHeight: '14rem',
          overflowY: 'auto',
          paddingRight: '0.25rem'
        }}>
              {manualPayments.map(p => {
            const due = safeDate(p?.dueDate);
            const label = p?.paymentType === 'bolt_on' ? p?.boltOnName || 'Bolt‑On' : p?.paymentType === 'membership_change' ? p?.label || 'Membership Change' : p?.label || 'Membership';
            const cadenceLabel = p?.paymentType === 'bolt_on' ? null : getCadenceLabel(p);
            return <li key={p.id} style={{
              marginBottom: '0.5rem'
            }}>
                    <strong>{label}</strong>
                    {p?.teamName && <span> · {p.teamName}</span>}
                    {cadenceLabel && <span> · {cadenceLabel}</span>}
                    {' — '}
                    {formatMoney(p?.finalAmount ?? p?.amount ?? 0, p?.currency || 'GBP')}
                    {' — '}
                    {due ? `due by ${due.toLocaleDateString('en-GB')}` : 'due soon'}
                    {p.boltOnEntry && <BoltOnRenewalSummary entry={p.boltOnEntry} currency={p.currency} />}
                    {p.periodStart && p.periodEnd && <div>Payment covers {safeDate(p.periodStart)?.toLocaleDateString('en-GB')} to {safeDate(p.periodEnd)?.toLocaleDateString('en-GB')}.</div>}
                    <button style={{
                marginLeft: '0.5rem'
              }} className="button small" onClick={() => handleManualPayment(p)} disabled={loading}>
                      Pay Now
                    </button>
                  </li>;
          })}
            </ul>
            {error && <p className="text-error" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </> : <>
            <p style={{
          color: 'var(--clr-text-muted)'
        }}>You have no outstanding manual payments.</p>
            {error && <p className="text-error" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </>}
      </Card>
    </>;
}
